/**
 * Behaviour shared by both product tiles:
 * <product-tile> (assets/product-tile.js) and
 * <product-tile-v2> (assets/product-tile-v2.js).
 *
 * A tile prices ONE concrete variant, exactly like the PDP — never
 * product.price, which is price_min across all variants and turns a single
 * variant with no market price into a 0 on the whole card. Liquid seeds
 * selected_or_first_available_variant (snippets/card-price.liquid); these
 * helpers keep that in step once a swatch or size changes which variant the
 * card stands for.
 *
 * A tile only has to expose what both elements already have: variants[],
 * colorOptionIdx, sizeOptionIdx, _selectedColor, _selectedSize — plus the
 * data-tile-price-* hooks that card-price.liquid renders.
 *
 * ONLY provably identical behaviour lives here. The two tiles diverge on
 * purpose and that divergence must not be "unified" away:
 *   - _handleCartClick — v2 always opens quick-view on mobile (its variant UI
 *     is hidden there); v1 submits the form. v1's carousel card is meant to
 *     carry less selection UX, so the branch stays out of the shared module.
 *   - _openQuickView — different trigger class per tile.
 *   - _updateSizeAvailability — v2 only. v1 deliberately never greys size pills
 *     per colour, and its colour swatches (<card-product-swatch> in theme.js)
 *     never touch _selectedColor at all.
 *
 * Loaded from layout/theme.liquid ahead of both tile scripts, after
 * scripts-tag.liquid brings in armada-money.js (defer preserves order).
 */
(function () {
  'use strict';

  const optionOf = (variant, index) => variant[`option${index + 1}`];
  const indexActive = (index) => !Number.isNaN(index) && index >= 0;

  const matches = (tile, variant, requireAvailable, withSize) => {
    if (requireAvailable && !variant.available) return false;
    if (
      indexActive(tile.colorOptionIdx) &&
      tile._selectedColor &&
      optionOf(variant, tile.colorOptionIdx) !== tile._selectedColor
    ) {
      return false;
    }
    if (
      withSize &&
      indexActive(tile.sizeOptionIdx) &&
      tile._selectedSize &&
      optionOf(variant, tile.sizeOptionIdx) !== tile._selectedSize
    ) {
      return false;
    }
    return true;
  };

  /**
   * The variant the card currently represents — an available match for the
   * selected colour + size, else a sold-out match (it still has a price to
   * show), else the first variant of the selected colour. With nothing
   * selected this lands on the first available variant, i.e. exactly the
   * variant Liquid priced, so the first repaint is a no-op rather than a flash.
   */
  const resolveDisplayVariant = (tile) => {
    const variants = tile.variants || [];
    return (
      variants.find((v) => matches(tile, v, true, true)) ||
      variants.find((v) => matches(tile, v, false, true)) ||
      variants.find((v) => matches(tile, v, false, false)) ||
      null
    );
  };

  /**
   * Repaint the tile price for that variant.
   *
   * Amounts only, never the layout: whether the tile rendered the plain or the
   * compare-at markup is the server's call. When a variant disagrees with the
   * rendered layout we blank the struck original rather than leave another
   * variant's compare-at price standing.
   *
   * Cents ride along in data-variants-json and are formatted with
   * armadaFormatMoney (assets/armada-money.js), the 1:1 mirror of
   * snippets/armada-money.liquid — so a client repaint cannot drift from the
   * server render in any currency.
   */
  const updatePrice = (tile) => {
    const format = window.armadaFormatMoney;
    const currentEl = tile.querySelector('[data-tile-price-current]');
    if (!currentEl || !tile.variants || !tile.variants.length || typeof format !== 'function') return;

    const variant = resolveDisplayVariant(tile);
    if (!variant || typeof variant.price !== 'number') return;

    currentEl.textContent = format(variant.price);

    const compareEl = tile.querySelector('[data-tile-price-compare]');
    if (!compareEl) return;

    const percentEl = tile.querySelector('[data-tile-price-percent]');
    const compareAt = typeof variant.compareAtPrice === 'number' ? variant.compareAtPrice : 0;

    if (compareAt > variant.price) {
      compareEl.textContent = format(compareAt);
      if (percentEl) {
        percentEl.textContent = `- ${Math.round((1 - variant.price / compareAt) * 100)}%`;
      }
    } else {
      compareEl.textContent = '';
      if (percentEl) percentEl.textContent = '';
    }
  };

  /** Available variants matching the tile's current colour + size selection. */
  const findMatchingVariants = (tile) =>
    (tile.variants || []).filter((v) => matches(tile, v, true, true));

  /** Seed the tile's selection from what Liquid rendered as active. */
  const syncSelectionFromDom = (tile) => {
    if (!tile._selectedColor && tile.dataset.defaultColor) {
      tile._selectedColor = tile.dataset.defaultColor;
    }
    if (!tile._selectedSize && tile.sizeBtns?.length) {
      const selected = tile.sizeBtns.find((b) => b.getAttribute('aria-checked') === 'true');
      if (selected) tile._selectedSize = selected.dataset.sizeValue;
    }
  };

  /** Wire the tile's "+" button to its own _handleCartClick, which differs per tile. */
  const initCartButton = (tile) => {
    if (!tile.cartBtn) return;
    tile.cartBtn.addEventListener('click', (e) => tile._handleCartClick(e));
  };

  window.ArmadaTile = {
    resolveDisplayVariant,
    updatePrice,
    findMatchingVariants,
    syncSelectionFromDom,
    initCartButton,
  };
})();
